import { Sparkles } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { DayRationale, UiLang } from "../lib/types";

/**
 * Hvorfor akkurat denne dagen ser ut som den gjør.
 *
 * Forklaringen appen ga var en varelistene — «Dag 3: Eidfjord, Undredal → Ål,
 * Ustaoset» — og den svarer ikke på spørsmålet. Den sier hva som endret seg,
 * ikke hvorfor det var riktig.
 *
 * Setningen her er satt sammen av fire tall optimalisereren faktisk har
 * regnet: dagens score, plasseringen blant dagene, hvor utsiktsavhengige
 * stoppene er, og om dagen endret innhold. Ingenting mer.
 *
 * Det den *ikke* sier er like bevisst. Den påstår aldri at et bestemt stopp
 * ble flyttet fra en dag til en annen, for algoritmen kan ikke gjøre det:
 * den flytter dagsgrenser over en fast rekkefølge, så et enkeltstopp bytter
 * ikke dag uten at alt etter det gjør det samme. «Vi flyttet Stegastein fra
 * onsdag til torsdag» ville vært en påstand datamodellen ikke dekker.
 */

interface Props {
  rationale?: DayRationale;
  /** Antall dager i planen — avgjør om «en av de klareste» er sant. */
  dayCount: number;
  lang: UiLang;
}

/** Over dette regnes stoppene på dagen som utsiktsavhengige. */
const VIEW_HEAVY = 0.6;

export function DayRationaleNote({ rationale, dayCount, lang }: Props) {
  const t = dictionaries[lang];

  // Uten optimalisering finnes ingen begrunnelse, og da skal det ikke stå noe.
  if (!rationale) return null;

  /*
   * «En av de klareste» krever at det faktisk finnes dager å være klarere enn.
   * På en tredagerstur er tredjeplass sisteplass, og øverste tredel er en
   * meningsløs ros — derfor toppen av rangeringen, ikke en andel.
   */
  const clearest = rationale.clearestRank === 1;
  const murkiest = rationale.clearestRank === dayCount && dayCount > 1;
  const viewHeavy = rationale.viewDependency >= VIEW_HEAVY;

  const text = !rationale.changed
    ? t.rationaleUnchanged
    : clearest && viewHeavy
      ? t.rationaleClearAndViewHeavy
      : clearest
        ? t.rationaleClear
        : murkiest
          ? t.rationaleMurky
          : t.rationaleRearranged;

  return (
    <p className="rationale">
      <Sparkles size={12} />
      <span>
        <strong>{t.rationaleTitle}</strong> {text}
      </span>
    </p>
  );
}
